import { useEffect, useRef, useState } from 'react';
import { BIG_SCREEN_PAGE_MS } from '../lib/big-screen';
import { wholeRowPages, type WholeRowPage } from '../lib/whole-row-pages';

/** Measure the untransformed content, then clip each page exactly after its last complete row. */
export function useWholeRowPaging<T extends HTMLElement = HTMLTableElement>(structure: string, holdEnd = false, rowSelector = 'tr') {
  const viewport = useRef<HTMLDivElement>(null);
  const content = useRef<T>(null);
  const [pages, setPages] = useState<WholeRowPage[]>([{ offset: 0, height: 0 }]);
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const viewportNode = viewport.current;
    const table = content.current;
    if (!viewportNode || !table) return;
    const measure = () => {
      viewportNode.style.setProperty('--whole-row-height', `${viewportNode.clientHeight}px`);
      const rows = Array.from(table.querySelectorAll<HTMLElement>(rowSelector));
      // offsetTop/offsetHeight round table rows to whole pixels. Use the actual
      // geometry so a collapsed border cannot expose a strip of the next score.
      const width = Number.parseFloat(getComputedStyle(viewportNode).width);
      const scale = width > 0 ? viewportNode.getBoundingClientRect().width / width : 1;
      const bounds = table.getBoundingClientRect();
      const rowBounds = rows.map(row => row.getBoundingClientRect());
      const next = wholeRowPages(
        rowBounds.map(row => (row.top - bounds.top) / scale), viewportNode.clientHeight,
        bounds.height / scale, rowBounds.map(row => (row.bottom - bounds.top) / scale),
      );
      setPages(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
      setIndex(holdEnd ? next.length - 1 : 0);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(viewportNode);
    observer.observe(table);
    measure();
    return () => observer.disconnect();
  }, [structure, holdEnd, rowSelector]);
  useEffect(() => {
    if (holdEnd || pages.length < 2) return;
    const timer = window.setInterval(() => setIndex(current => (current + 1) % pages.length), BIG_SCREEN_PAGE_MS);
    return () => window.clearInterval(timer);
  }, [pages.length, holdEnd]);
  return { viewport, content, page: pages[index] ?? pages[0]! };
}
